import React, { useState } from 'react';
import { User, Lock, AlertCircle, LogIn } from 'lucide-react';

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      setError('Por favor complete todos los campos');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/sistemasms/backend/api/auth/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(credentials)
      });

      const result = await response.json();

      if (result.message === 'Login successful') {
        onLogin(result.user);
      } else {
        setError(result.message || 'Error al iniciar sesión');
      }
    } catch (error) {
      setError('Error de conexión. Verifique que el servidor esté ejecutándose.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    wrapper: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 1rem',
      background: 'linear-gradient(135deg, #1e3a5f 0%, #312e81 50%, #581c87 100%)',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    card: {
      maxWidth: '420px',
      width: '100%',
      background: 'rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      padding: '2.5rem',
      borderRadius: '2rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
    },
    iconCircle: {
      width: '5rem',
      height: '5rem',
      background: 'linear-gradient(135deg, #60a5fa, #a855f7)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
      boxShadow: '0 10px 25px rgba(96, 165, 250, 0.3)',
    },
    title: {
      textAlign: 'center',
      fontSize: '1.875rem',
      fontWeight: '800',
      color: '#ffffff',
      margin: '0 0 0.5rem',
      letterSpacing: '-0.025em',
    },
    subtitle: {
      textAlign: 'center',
      fontSize: '0.875rem',
      color: '#a5b4fc',
      margin: '0 0 2rem',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem',
    },
    inputGroup: {
      position: 'relative',
    },
    inputIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#a5b4fc',
      pointerEvents: 'none',
    },
    input: {
      width: '100%',
      padding: '0.875rem 1rem 0.875rem 3rem',
      border: '1px solid rgba(129, 140, 248, 0.3)',
      backgroundColor: 'rgba(30, 27, 75, 0.4)',
      color: '#ffffff',
      borderRadius: '1rem',
      fontSize: '0.875rem',
      outline: 'none',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
    },
    errorBox: {
      borderRadius: '1rem',
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      border: '1px solid rgba(239, 68, 68, 0.5)',
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    errorText: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#fecaca',
      margin: 0,
    },
    button: {
      width: '100%',
      padding: '1rem',
      border: 'none',
      borderRadius: '1rem',
      fontSize: '0.875rem',
      fontWeight: '700',
      color: '#1e1b4b',
      background: 'linear-gradient(to right, #dbeafe, #e9d5ff)',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
      transform: 'translateY(0)',
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    spinner: {
      display: 'inline-block',
      width: '1.25rem',
      height: '1.25rem',
      border: '2px solid transparent',
      borderTop: '2px solid #1e1b4b',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginRight: '0.75rem',
      verticalAlign: 'middle',
    },
  };

  return (
    <div style={styles.wrapper}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .login-input::placeholder {
          color: #818cf8;
          opacity: 0.7;
        }
        .login-input:focus {
          border-color: #a855f7 !important;
          box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.3) !important;
        }
        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 0 30px rgba(255, 255, 255, 0.4);
          background: linear-gradient(to right, #ffffff, #ffffff);
        }
      `}</style>

      <div style={styles.card}>
        <div style={styles.iconCircle}>
          <LogIn style={{ width: '2.5rem', height: '2.5rem', color: '#ffffff' }} />
        </div>

        <h2 style={styles.title}>Bienvenido</h2>
        <p style={styles.subtitle}>Sistema de Mensajería Masiva WhatsApp</p>

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <div style={styles.inputIcon}>
              <User style={{ width: '1.25rem', height: '1.25rem' }} />
            </div>
            <input
              id="username"
              name="username"
              type="text"
              required
              className="login-input"
              style={styles.input}
              placeholder="Usuario"
              value={credentials.username}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          <div style={styles.inputGroup}>
            <div style={styles.inputIcon}>
              <Lock style={{ width: '1.25rem', height: '1.25rem' }} />
            </div>
            <input
              id="password"
              name="password"
              type="password"
              required
              className="login-input"
              style={styles.input}
              placeholder="Contraseña"
              value={credentials.password}
              onChange={handleChange}
              disabled={loading}
            />
          </div>

          {error && (
            <div style={styles.errorBox}>
              <AlertCircle style={{ width: '1.25rem', height: '1.25rem', color: '#fca5a5', flexShrink: 0 }} />
              <p style={styles.errorText}>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="login-btn"
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {}),
            }}
          >
            {loading ? (
              <span>
                <span style={styles.spinner}></span>
                Iniciando sesión...
              </span>
            ) : (
              'Iniciar Sesión'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
